import {
  AdminTableHeader,
  adminFilterDateInputClasses,
  adminFilterDropdownTriggerClass,
} from '../shared/adminUx';
/** Fixed PDF report library — mock seed data (replace with API). */

export const REPORT_AREAS = [
  'Platform',
  'Moderation',
  'Compliance',
  'Fraud & Security',
  'Support Tickets',
  'Billing & Subscriptions',
  'Customers',
  'Businesses',
  'Posts',
  'Reviews',
];

export const FREQUENCY = {
  MONTHLY: 'Monthly',
  ON_DEMAND: 'On-Demand',
};

/** @typedef {{
 *   id: string;
 *   reportName: string;
 *   reportArea: string;
 *   frequency: string;
 *   reportPeriod: string;
 *   generatedOn: string;
 *   generatedBy: string;
 *   format: string;
 *   status?: 'Ready' | 'Generating' | 'Failed';
 *   notes?: string;
 *   template: 'moderation-summary' | 'generic';
 * }} ReportRow */

/** @type {ReportRow[]} */
export const REPORT_LIBRARY_SEED = [
  {
    id: 'R-PO-2026-01',
    reportName: 'Platform Summary',
    reportArea: 'Platform',
    frequency: FREQUENCY.MONTHLY,
    reportPeriod: 'January 2026',
    generatedOn: '2026-02-01',
    generatedBy: 'System',
    format: 'PDF',
    status: 'Ready',
    notes: 'Monthly platform summary for internal reference.',
    template: 'generic',
  },
  {
    id: 'R-MOD-2026-01',
    reportName: 'Moderation Summary',
    reportArea: 'Moderation',
    frequency: FREQUENCY.MONTHLY,
    reportPeriod: 'January 2026',
    generatedOn: '2026-02-01',
    generatedBy: 'System',
    format: 'PDF',
    status: 'Ready',
    notes: 'Monthly moderation summary for internal reference.',
    template: 'moderation-summary',
  },
  {
    id: 'R-COMP-2026-01',
    reportName: 'Compliance Summary',
    reportArea: 'Compliance',
    frequency: FREQUENCY.MONTHLY,
    reportPeriod: 'January 2026',
    generatedOn: '2026-02-01',
    generatedBy: 'System',
    format: 'PDF',
    status: 'Ready',
    template: 'generic',
  },
  {
    id: 'R-FS-2026-01',
    reportName: 'Fraud & Security Summary',
    reportArea: 'Fraud & Security',
    frequency: FREQUENCY.MONTHLY,
    reportPeriod: 'January 2026',
    generatedOn: '2026-02-01',
    generatedBy: 'System',
    format: 'PDF',
    status: 'Ready',
    template: 'generic',
  },
  {
    id: 'R-SUP-2026-01',
    reportName: 'Support Tickets Summary',
    reportArea: 'Support Tickets',
    frequency: FREQUENCY.MONTHLY,
    reportPeriod: 'January 2026',
    generatedOn: '2026-02-01',
    generatedBy: 'System',
    format: 'PDF',
    status: 'Ready',
    template: 'generic',
  },
  {
    id: 'R-FIN-2026-01',
    reportName: 'Billing & Subscriptions Summary',
    reportArea: 'Billing & Subscriptions',
    frequency: FREQUENCY.MONTHLY,
    reportPeriod: 'January 2026',
    generatedOn: '2026-02-01',
    generatedBy: 'System',
    format: 'PDF',
    status: 'Ready',
    template: 'generic',
  },
  {
    id: 'R-MOD-OD-01',
    reportName: 'Moderation Case Summary',
    reportArea: 'Moderation',
    frequency: FREQUENCY.ON_DEMAND,
    reportPeriod: '01 Jan 2026 - 15 Jan 2026',
    generatedOn: '2026-01-16',
    generatedBy: 'Moderation — ABC Name',
    format: 'PDF',
    status: 'Ready',
    notes: 'Focused window for escalations review.',
    template: 'moderation-summary',
  },
  {
    id: 'R-COMP-OD-01',
    reportName: 'Compliance Expiry Summary',
    reportArea: 'Compliance',
    frequency: FREQUENCY.ON_DEMAND,
    reportPeriod: '01 Jan 2026 - 31 Jan 2026',
    generatedOn: '2026-01-31',
    generatedBy: 'Compliance — ABC Name',
    format: 'PDF',
    status: 'Ready',
    notes: 'January compliance expiry summary',
    template: 'generic',
  },
];

export function formatDateDisplay(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** On-demand report title from module + selected period (matches table period wording). */
export function buildOnDemandReportName(reportArea, dateFrom, dateTo) {
  const from = formatDateDisplay(dateFrom);
  const to = formatDateDisplay(dateTo);
  return `${reportArea} Report (${from} - ${to})`;
}

export function scopeLabel(generatedBy, frequency) {
  if (frequency === FREQUENCY.MONTHLY && generatedBy === 'System') return 'System';
  return generatedBy;
}

export function nextGeneratedId() {
  return `R-GEN-${Date.now()}`;
}

const STORAGE_KEY = 'tp-admin-reports-extra';

export function getExtraReports() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveExtraReport(row) {
  const cur = getExtraReports().filter((r) => r.id !== row.id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([row, ...cur]));
}

export function getAllReportsMerged() {
  const seedIds = new Set(REPORT_LIBRARY_SEED.map((r) => r.id));
  const extras = getExtraReports().filter((r) => !seedIds.has(r.id));
  return [...REPORT_LIBRARY_SEED, ...extras];
}

export function findReportById(id) {
  if (!id) return null;
  const fromSeed = REPORT_LIBRARY_SEED.find((r) => r.id === id);
  if (fromSeed) return fromSeed;
  return getExtraReports().find((r) => r.id === id) || null;
}
